// src/components/voices/DefaultVoiceCard.tsx
/**
 * DefaultVoiceCard Component
 *
 * Displays a pre-made ElevenLabs voice card with preview player.
 * These voices are available to all users without cloning.
 */

import { useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Mic, CheckCircle, Globe } from 'lucide-react';
import { VoicePreviewPlayer } from './VoicePreviewPlayer';
import type { IDefaultVoice } from '@/@types/voices';
import { cn } from '@/lib/utils';

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Format use case for display
 */
function formatUseCase(useCase: string): string {
  return useCase
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Get gender badge color
 */
function getGenderBadgeVariant(gender: 'male' | 'female'): 'default' | 'secondary' {
  return gender === 'female' ? 'default' : 'secondary';
}

// ============================================================================
// Component
// ============================================================================

function DefaultVoiceCard({
  voice,
  isSelected,
  onSelect,
}: {
  voice: IDefaultVoice;
  isSelected?: boolean;
  onSelect?: (voiceId: string) => void;
}) {
  const handleSelect = useCallback(() => {
    onSelect?.(voice.id);
  }, [onSelect, voice.id]);

  return (
    <Card
      className={cn(
        'overflow-hidden hover:shadow-md transition-all cursor-pointer',
        isSelected && 'ring-2 ring-blue-500'
      )}
      onClick={handleSelect}
    >
      {/* Header with Voice Icon */}
      <div className="relative bg-gradient-to-br from-purple-950/30 to-pink-950/30 light:from-purple-50 light:to-pink-50 p-6">
        <div className="flex items-center justify-center">
          <div className="p-4 bg-card rounded-full shadow-sm">
            <Mic className="w-8 h-8 text-purple-400 light:text-purple-600" />
          </div>
        </div>

        {/* Pre-made Badge */}
        <div className="absolute top-3 right-3">
          <Badge variant="outline" className="flex items-center gap-1 bg-card/80">
            <Globe className="w-3 h-3" />
            Pre-made
          </Badge>
        </div>

        {/* Selected Indicator */}
        {isSelected && (
          <div className="absolute top-3 left-3">
            <div className="p-1 bg-blue-500 rounded-full">
              <CheckCircle className="w-4 h-4 text-white" />
            </div>
          </div>
        )}
      </div>

      <CardHeader className="pb-2">
        <CardTitle className="text-base truncate">{voice.name}</CardTitle>
        <p className="text-sm text-muted-foreground line-clamp-2">{voice.description}</p>
      </CardHeader>

      <CardContent className="py-0 space-y-3">
        {/* Voice Info Badges */}
        <div className="flex flex-wrap gap-1.5">
          <Badge variant={getGenderBadgeVariant(voice.gender)} className="text-xs">
            {voice.gender === 'female' ? '♀ Female' : '♂ Male'}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {voice.accent}
          </Badge>
          <Badge variant="outline" className="text-xs">
            {formatUseCase(voice.useCase)}
          </Badge>
        </div>

        {/* Preview Player */}
        <div
          role="presentation"
          className="pt-2 pb-4"
          onClick={e => e.stopPropagation()}
          onKeyDown={e => e.stopPropagation()}
        >
          <VoicePreviewPlayer previewUrl={voice.previewUrl} voiceName={voice.name} />
        </div>
      </CardContent>
    </Card>
  );
}

export { DefaultVoiceCard };
